import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import {
  callAlertWeb,
  callNativeWeb,
  footerShowEventWeb,
  uploadPhotoAddressWeb
} from 'common/appInterface';
import client from 'lib/CommonApi/client';
import { AxiosError } from 'axios';
import { Location } from 'history';
import Modal from 'components/Modal';
import { CardProductCode } from '../../constants/Card';
import { SuccessResponseCodeList } from '../../constants/Common';
import { useLoading } from 'components/Loading/LoadingProvider';

// DeliverySelect.tsx 를 참고하여 만들었으며 추 후 DeliverySelect.tsx 삭제 예정
const CardDeliverySelect = () => {
  const screenId = "APPL11";
  /** Location State */
  const location = useLocation() as Location<LocationState>;

  const navigate = useNavigate();

  const { showLoading, hideLoading } = useLoading();

  const [isModal, setModal] = useState(false);
  const [cardApply, setCardApply] = useState<Partial<CardApplyStep6>>(location.state.apply);
  const [displayCardIssueFee, setDisplayCardIssueFee] = useState<number | null>(null);

  const {t, i18n} = useTranslation();

  /**
   * dhbyun
   * 표기 카드 발급비 조회
   */
  const inquiryDisplayCardIssueFeeApi = useCallback(
    async () => {
      await client.get(`/api/app/apply/${cardApply.cardProductCode}/issueFee`, { params: location.state.query }).then((response) => {
        if(response.data.response) {
          setDisplayCardIssueFee(response.data.response.cardIssueFee);
        }
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      });
    },
    [cardApply.cardProductCode, location]
  );

  /** 주소증촬영 요청 (uploadPhotoAddress_Web (WEB > APP)) */
  const callUploadPhotoAddress = useCallback(() => {
    uploadPhotoAddressWeb();
  }, []);

  /** 카드신청정보 확인완료 */
  const clickModalYes = useCallback(async () => {
    showLoading();
    // 카드신청내역 등록
    await client
      .patch('/api/app/apply/history', {
        ...location.state.query,
        receiveSeq: cardApply.receiveSeq,
        receiveDate: cardApply.receiveDate,
        receiveStatusCode: '07',
        sendTypeDivisionCode:
          location.state.apply.receiveMethod === '01' ? '1' : '3', // 카드 수령 방법이 직접 수령(01)인 경우 본인 수령 "1", 카드 수령 방법이 우편함 배송(02)인 경우 지정영업점배송 "3"
        deliverySelectCode: '1',
        thirdPartyReceiptYn: cardApply.thirdPartyReceiptYn,
        addressImageCode: '5',
        cardProductCode: cardApply.cardProductCode,
        addressChangeYn: cardApply.isChangeDelivery ? 'Y' : 'N',
        receiveMethod: location.state.apply.receiveMethod
      })
      .then(async (response) => {
        if (response.data.code === '00') {
          // 수령지변경은 메인으로 이동
          if (cardApply.isChangeDelivery) {
            navigate('/main', {
              state: {
                query: location.state.query,
                swiperIndex: location.state.swiperIndex,
                tabIndex: location.state.tabIndex
              }
            });
          } else {
            switch (Number(cardApply.cardProductCode)) {
              case CardProductCode.DreamCard:
              case CardProductCode.PremierCard:
                navigate('/apply/step7', {
                  state: {
                    query: location.state.query,
                    apply: {
                      isChangeDelivery: false,
                      callPage: 'cardApply',
                      cardApply: cardApply
                    }
                  }
                });
                break;
              case CardProductCode.WelcomeCard:
                if (
                  cardApply.requestIssueDivisionCode?.startsWith('2') ||
                  cardApply.requestIssueDivisionCode?.startsWith('3')
                ) {
                  navigate('/apply/complet', {
                    state: { ...location.state, apply: cardApply }
                  });
                } else {
                  await client
                    .post('/api/app/apply/chargeCardIssueFee', {
                      ...location.state.query,
                      receiveSeq: cardApply.receiveSeq,
                      receiveDate: cardApply.receiveDate
                    })
                    .then((res) => {
                      if (SuccessResponseCodeList.includes(res.data.code)) {
                        if (res.data.response.bCharged === true) {
                          navigate('/apply/complet', {
                            state: { ...location.state, apply: cardApply }
                          });
                        } else {
                          navigate('/apply/depositCardIssuanceFee', {
                            state: {
                              ...location.state,
                              apply: cardApply,
                              storage: {
                                ...location.state.storage,
                                chargeCardIssueFeeRes: res.data.response,
                                displayCardIssueFee: displayCardIssueFee
                              }
                            }
                          });
                        }
                      } else {
                        callAlertWeb('ERROR', res.data.code, res.data.message);
                      }
                    })
                    .catch((e: AxiosError) => {
                      callAlertWeb('ERROR', '99', e.message);
                    });
                }
                break;
              case CardProductCode.DirectCard:
              default:
                navigate('/apply/complet', {
                  state: { ...location.state, apply: cardApply }
                });
                break;
            }
          }
        }
      })
      .catch((e: AxiosError) => {
        callAlertWeb('ERROR', '99', e.message);
      })
      .finally(() => {
        hideLoading();
      });
  }, [
    location,
    navigate,
    cardApply,
    displayCardIssueFee,
    showLoading,
    hideLoading
  ]);

  /** Next 클릭 */
  const clickNext = useCallback((delivery: string) => {
    // 사진업로드
    if (delivery === "1") {
      callUploadPhotoAddress();
    }
    // 직접입력
    else if (delivery === "3") {
      navigate("/apply/step6/input/search", { state: { ...location.state, apply: { ...cardApply, deliverySelectCode: delivery }}})
    }
  }, [navigate, location, cardApply, callUploadPhotoAddress]);

  /** 주소촬영 결과전달 (completedPhotoAddress_App (APP > WEB)) */
  const callbackCompletedPhotoAddress = useCallback(
    (event) => {
      setCardApply((value) => ({
        ...value,
        thirdPartyReceiptYn: event.detail.receiveRelationCheck
      }));

      showLoading();
      client
        .post('/api/app/apply/address/upload', {
          ...location.state.query,
          receiveSeq: cardApply.receiveSeq,
          receiveDate: cardApply.receiveDate,
          addressImageValue: decodeURIComponent(event.detail.imageValue),
          detailAddr: event.detail.detailAddr
        })
        .then((response) => {
          if (response.data.code === '00') {
            setModal(true);
          } else {
            callAlertWeb('ERROR', response.data.code, response.data.message);
          }
        })
        .catch((e: AxiosError) => {
          callAlertWeb('ERROR', '99', e.message);
        })
        .finally(() => {
          hideLoading();
        });
    },
    [location, cardApply, showLoading, hideLoading]
  );

  const callbackBackKeyEvent = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // TODO 테스트용
  const testCall = useCallback(() => {
    let url = `${process.env.REACT_APP_LOCALHOST}/idcard-upload-1.png`;
    client.get(url, { responseType: 'arraybuffer' }).then((response) => {
      let imageValue = Buffer.from(response.data, "base64").toString("base64");
      let event = new CustomEvent("addressUpload", { detail: { receiveRelationCheck: "Y", imageValue: encodeURIComponent(imageValue) }});
      document.dispatchEvent(event);
    });
  }, []);

  // 주소촬영 결과전달 이벤트 리스너 (completedPhotoAddress_App (APP > WEB))
  useEffect(() => {
    document.addEventListener("addressUpload", callbackCompletedPhotoAddress);
    return () => document.removeEventListener("addressUpload", callbackCompletedPhotoAddress);
  }, [callbackCompletedPhotoAddress]);

  useEffect(() => {
    document.addEventListener("backpress", callbackBackKeyEvent);
    return () => document.removeEventListener("backpress", callbackBackKeyEvent);
  }, [callbackBackKeyEvent]);

  useEffect(() => {
    // 언어설정
    i18n.changeLanguage(location.state.query.languageCode);
    // callNative_Web 처리 (WEB > APP)
    callNativeWeb(screenId, t(screenId + ".title", "Card Receiving Address"));
    // 하단바 숨기기
    footerShowEventWeb("N");

    inquiryDisplayCardIssueFeeApi();
    // eslint-disable-next-line
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <p style={{fontSize: '16px', fontWeight: 400, margin: '16px', color: '#111111'}}>{t(screenId + '.explan01', 'Please choose the option to receive your card.')}</p>
      <div style={{height: '1px', background: '#c1c1c1'}} />
      <div>
        <button style={{width: '100%', textAlign: 'left', paddingLeft: '16px', paddingRight: '16px', paddingTop: '24px', paddingBottom: '24px'}}
                onClick={() => {
                  clickNext("1");
                }}>
          <span style={{ alignItems: 'center', display: 'flex' }}>
            <img style={{ width: '24px', height: '24px' }} src={'/assets/images/ico/mdi_camera-outline.png'} alt="" />
            <div style={{ width: '100%', marginLeft: '12px' }}>
              <p style={{
                fontSize: '20px',
                fontWeight: 700,
                color: '#111111'
              }}>{t(screenId + '.selectHeader01', 'Upload "Address" photo')}</p>
            <p style={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#666666'
            }}>{t(screenId + '.selectExplan01', 'Please take a picture of address and upload it. Card will be delivered to the provided address.')}</p>
            </div>
            <img style={{ width: '16px', height: '16px' }} src={'/assets/images/ico/arrow_right_new.png'} alt="" />
          </span>
        </button>
      </div>
      <div style={{ height: '1px', background: '#c1c1c1' }} />
      <div>
        <button style={{
          width: '100%',
          textAlign: 'left',
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingTop: '24px',
          paddingBottom: '24px'
        }}
                onClick={() => {
                  clickNext("3");
                }}>
          <span style={{ alignItems: 'center', display: 'flex' }}>
            <img style={{ width: '24px', height: '24px' }} src={'/assets/images/ico/uil_edit.png'} alt="" />
            <div style={{ width: '100%', marginLeft: '12px' }}>
              <p style={{
                fontSize: '20px',
                fontWeight: 700,
                color: '#111111'
              }}>{t(screenId + '.selectHeader03', 'Enter Receiving Address')}</p>
            <p style={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#666666'
            }}>{t(screenId + '.selectExplan03', 'Please enter your address manually to receive the card.')}</p>
            </div>
            <img style={{ width: '16px', height: '16px' }} src={'/assets/images/ico/arrow_right_new.png'} alt="" />
          </span>
        </button>
      </div>
      <div style={{ height: '1px', background: '#c1c1c1' }} />
      {process.env.REACT_APP_PROFILE !== "prod" &&
        <button className="btnPrimaryTy01" style={{width: '100px'}} type="button" onClick={testCall}><span>주소업로드</span></button>
      }
      <Modal isOpen={isModal} contentLabel="modal" onRequestClose={clickModalYes} className="popTyLayer2" overlayClassName="dim" closeTimeoutMS={500}>
        <p className="message">{t("APPL11.popup06")}</p>
        <dl>
          <dd><span></span></dd>
          <dd className="detail"></dd>
        </dl>
        <div className="btnWrapTy02">
          <button type="button" className="btnPrimaryTy01" onClick={clickModalYes}><span>{t("COMMON.button.confirm")}</span></button>
        </div>
      </Modal>
    </div>
  );
};

export default CardDeliverySelect;